import { DECOR_VARIANTS } from '@cloudpearl/config';
import type { DecorKind } from '@cloudpearl/config';
import { Rng, STREAM, hash32 } from './rng';
import type { World } from './world';

/** A purely visual, passable prop. Deterministic per seed so friends see the same world. */
export interface DecorItem {
  kind: DecorKind;
  variant: number;
  x: number;
  y: number;
  z: number;
  scale: number;
  /** Rotation about Y (render-only, radians). */
  yaw: number;
}

export const DECOR_CELL = 48;

/**
 * Decor for one 48 m cell. `density` thins props on low-end devices by a stable per-item random;
 * `only` restricts the result to some kinds (e.g. the far ring only shows sky islands).
 * Render-only, so it may use transcendental functions freely.
 */
export function decorForCell(world: World, ci: number, cj: number, density = 1, only?: ReadonlySet<string>): DecorItem[] {
  const map = world.map;
  const out: DecorItem[] = [];
  const rng = new Rng(world.seed, STREAM.decor, hash32(ci, cj, 5));
  const x0 = ci * DECOR_CELL;
  const z0 = cj * DECOR_CELL;
  const area = (DECOR_CELL * DECOR_CELL) / 10000;

  for (const spawn of map.decor) {
    if (spawn.kind === 'waterfall') continue;
    const per = spawn.cluster ? (spawn.cluster[0] + spawn.cluster[1]) / 2 : 1;
    const expected = (spawn.density * area) / per;
    let n = Math.floor(expected);
    if (rng.float() < expected - n) n++;
    for (let k = 0; k < n; k++) {
      const cx = x0 + rng.float() * DECOR_CELL;
      const cz = z0 + rng.float() * DECOR_CELL;
      const members = spawn.cluster ? rng.int(spawn.cluster[0], spawn.cluster[1]) : 1;
      for (let m = 0; m < members; m++) {
        const keep = rng.float();
        const x = cx + (rng.float() - 0.5) * (members > 1 ? 9 : 0);
        const z = cz + (rng.float() - 0.5) * (members > 1 ? 9 : 0);
        const scale = rng.range(spawn.scale[0], spawn.scale[1]);
        const variant = rng.int(0, DECOR_VARIANTS[spawn.kind] - 1);
        const yaw = rng.float() * Math.PI * 2;
        const band = spawn.lift ? spawn.lift[rng.int(0, spawn.lift.length - 1)]! : null;
        const lift = band ? rng.range(band[0], band[1]) : 0;
        if (keep > density) continue;
        // `only`: just these kinds (filtered after every random draw, so the rest stay identical).
        if (only && !only.has(spawn.kind)) continue;
        if (spawn.kind === 'skyIsland') {
          // Background islands live far above or below the playable layer.
          out.push({ kind: 'skyIsland', variant, x, y: lift, z, scale, yaw });
          continue;
        }
        if (!world.solidInset(x, z, spawn.inset ?? 1)) continue;
        if (spawn.maxSlope !== undefined) {
          const sx = world.height(x + 1, z) - world.height(x - 1, z);
          const sz = world.height(x, z + 1) - world.height(x, z - 1);
          if ((sx * sx + sz * sz) / 4 > spawn.maxSlope * spawn.maxSlope) continue;
        }
        out.push({ kind: spawn.kind, variant, x, y: world.height(x, z), z, scale, yaw });
      }
    }
  }

  // Waterfalls pour off island rims. Each island emits its falls from the cell holding its centre.
  const wf = map.decor.find((d) => d.kind === 'waterfall');
  if (wf && world.islands && (!only || only.has('waterfall'))) {
    for (const isl of world.islandsNear(x0 + DECOR_CELL / 2, z0 + DECOR_CELL / 2, [])) {
      if (Math.floor(isl.cx / DECOR_CELL) !== ci || Math.floor(isl.cz / DECOR_CELL) !== cj) continue;
      const r2 = new Rng(world.seed, STREAM.decor, hash32(isl.id, 31));
      let count = Math.floor(wf.density);
      if (r2.float() < wf.density - count) count++;
      for (let k = 0; k < count; k++) {
        const ang = r2.float() * Math.PI * 2;
        const dx = Math.cos(ang);
        const dz = Math.sin(ang);
        let rx = isl.cx;
        let rz = isl.cz;
        for (let s = 0; s < 120; s++) {
          if (world.mask(rx + dx * 1.5, rz + dz * 1.5) < 0.5) break;
          rx += dx * 1.5;
          rz += dz * 1.5;
        }
        const scale = r2.range(wf.scale[0], wf.scale[1]);
        // Waterfall prefabs flow along local +X; rotation.y = −angle points that outward.
        out.push({ kind: 'waterfall', variant: 0, x: rx - dx * 0.8, y: world.height(rx - dx * 2, rz - dz * 2), z: rz - dz * 0.8, scale, yaw: -ang });
      }
    }
  }
  return out;
}
